import type { PersonStatus, Thumb } from '@zinc/shared';
import { ScoreChip } from '../ScoreChip';
import { formatTime } from './SolveHeader';

function ThumbIcon({ down = false }: { down?: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={down ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3Z" />
      <path d="M7 10l4-7a2.5 2.5 0 0 1 2.5 2.5V9h5.2a2 2 0 0 1 2 2.3l-1.2 7.6a2 2 0 0 1-2 1.7H7" />
    </svg>
  );
}

/** The finish screen: times (the other person's only now that you've finished), the score,
 * nudges, the puzzle's finish message, thumbs up or down, add to a collection, and replay. */
export function FinishDialog(props: {
  timeMs: number;
  nudges: number;
  score: number | null;
  otherName: string | null;
  other: PersonStatus | null;
  message: string | null;
  rating: Thumb | null;
  onRate: (rating: Thumb | null) => void;
  onAddToCollection: () => void;
  onReplay: () => void;
  onLibrary: () => void;
  onClose: () => void;
}) {
  const thumb = (value: Thumb, label: string) => {
    const on = props.rating === value;
    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={on}
        onClick={() => props.onRate(on ? null : value)}
        className={`flex h-11 w-11 items-center justify-center rounded-btn border ${on ? 'border-accent bg-accent-tint text-accent-dark' : 'border-line bg-surface text-ink'}`}
      >
        <ThumbIcon down={value === 'down'} />
      </button>
    );
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(31,30,28,0.35)] p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="finish-title"
    >
      <div className="flex max-h-full w-full max-w-[420px] flex-col gap-3 overflow-y-auto rounded-[20px] border border-line bg-surface px-5 py-5">
        <h2 id="finish-title" className="m-0 text-[22px] font-extrabold">
          Solved
        </h2>
        <dl className="m-0 grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-1.5 text-[15px]">
          <dt className="text-muted">Your time</dt>
          <dd className="m-0 font-semibold tabular-nums">{formatTime(props.timeMs)}</dd>
          {props.otherName && (
            <>
              <dt className="text-muted">{props.otherName}</dt>
              <dd className="m-0 font-semibold tabular-nums">
                {props.other?.progress === 'solved' && props.other.timeMs !== null
                  ? formatTime(props.other.timeMs)
                  : props.other?.progress === 'in-progress'
                    ? 'in progress'
                    : 'not solved yet'}
              </dd>
            </>
          )}
          <dt className="text-muted">Score</dt>
          <dd className="m-0">
            <ScoreChip score={props.score} />
          </dd>
          <dt className="text-muted">Nudges</dt>
          <dd className="m-0 font-semibold">{props.nudges}</dd>
        </dl>
        {props.message && (
          <p className="m-0 rounded-[12px] bg-bg px-3 py-2.5 text-[15px]">{props.message}</p>
        )}
        <div className="flex items-center gap-2">
          <span className="text-[14px] text-muted">Did you enjoy it?</span>
          <span className="ml-auto flex gap-2">
            {thumb('up', 'Thumbs up')}
            {thumb('down', 'Thumbs down')}
          </span>
        </div>
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={props.onAddToCollection}
            className="h-12 shrink-0 rounded-[12px] border border-line bg-surface text-[15px] font-semibold text-ink"
          >
            Add to collection
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={props.onReplay}
              className="h-12 flex-1 rounded-[12px] border border-line bg-surface text-[15px] font-semibold text-ink"
            >
              Replay
            </button>
            <button
              type="button"
              onClick={props.onClose}
              className="h-12 flex-1 rounded-[12px] border border-line bg-surface text-[15px] font-semibold text-ink"
            >
              Look at the grid
            </button>
          </div>
          <button
            type="button"
            onClick={props.onLibrary}
            className="h-12 rounded-[12px] border-0 bg-accent text-[15px] font-bold text-white"
          >
            Library
          </button>
        </div>
      </div>
    </div>
  );
}
